import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import ReceiptIcon from '@mui/icons-material/Receipt';

import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { useCart } from '../../contexts/CartContext';
import ProductDetailsModal from '../../components/ProductDetailsModal/ProductDetailsModal';
import { getActiveProducts, searchProducts } from '../../services/product-service';
import { Product } from '../../types';
import styles from './ResellerStorePage.module.css';

// Define these interfaces to match the ProductDetailsModal component
interface ProductImage {
  id: string;
  image_url: string;
  alt_text?: string;
  is_primary: boolean;
  display_order: number;
  product_id: string;
}

interface ProductWithImages {
  id: string;
  sku: string;
  name: string;
  price: number;
  is_active: boolean;
  description?: string;
  category?: string;
  images?: ProductImage[];
  specifications?: Record<string, any>;
  manual?: string;
  model?: string;
  reseller_commission_rate?: number;
}

// Function to convert Product to ProductWithImages
const convertToProductWithImages = (product: Product): ProductWithImages => {
  return {
    ...product,
    images: product.images?.map(img => ({
      id: img.id,
      image_url: img.image_url,
      alt_text: img.alt_text || product.name, // Provide a fallback
      is_primary: img.is_primary,
      display_order: img.display_order,
      product_id: img.product_id,
    })),
    reseller_commission_rate: product.reseller_commission_rate,
  };
};

// Mock data for categories
const MOCK_CATEGORIES = [
  { id: 1, name: 'Gamer', image: '/images/image-19.png' },
  { id: 2, name: 'Aspire', image: '/images/image-19.png' },
  { id: 3, name: 'Premium', image: '/images/image-19.png' },
  { id: 4, name: 'Monitores', image: '/images/image-19.png' },
  { id: 5, name: 'Acessórios', image: '/images/image-19.png' },
  { id: 6, name: 'Inteligência Artificial', image: '/images/image-19.png' },
  { id: 7, name: 'Softwares', image: '/images/image-19.png' },
];

// Price utility functions
const formatCurrency = (value: number | null) => {
  if (value === null) return 'N/A';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

const ITEMS_PER_PAGE = 20;

const ResellerStorePage = () => {
  const navigate = useNavigate();
  const { setBreadcrumbs } = useBreadcrumb();
  const { addToCart } = useCart();
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [selectedProduct, setSelectedProduct] = useState<ProductWithImages | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showAddedAlert, setShowAddedAlert] = useState(false);

  // State for products
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // State for search
  const [searchQuery, setSearchQuery] = useState('');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const requestRef = useRef(0);

  // State for pagination
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // Set up breadcrumbs
  useEffect(() => {
    setBreadcrumbs([{ label: 'Loja do revendedor' }]);

    return () => {
      setBreadcrumbs([]);
    };
  }, [setBreadcrumbs]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Fetch products on mount, search change, and when the next page is requested.
  useEffect(() => {
    let isCurrentRequest = true;
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;

    const fetchProducts = async () => {
      setLoading(true);
      setError(null);

      try {
        // If there's a search query, use searchProducts
        if (searchQuery) {
          console.log(`Searching for products with query: ${searchQuery}`);
          const result = await searchProducts(searchQuery, page, ITEMS_PER_PAGE);
          console.log('Search result:', result);
          if (!isCurrentRequest || requestId !== requestRef.current) {
            return;
          }

          if (result) {
            setProducts(prevProducts => {
              if (page === 1) {
                return result.items;
              }

              const existingProductIds = new Set(prevProducts.map(product => product.id));
              const nextProducts = result.items.filter(
                product => !existingProductIds.has(product.id)
              );

              return [...prevProducts, ...nextProducts];
            });
            setHasMore(Boolean(result.hasMore));
            console.log(
              `Found ${result.items.length} active products out of ${result.total} total`
            );
            console.log('Products detail:', JSON.stringify(result.items, null, 2));
          } else {
            setHasMore(false);
          }
        } else {
          console.log(`Fetching active products, page ${page}`);
          const result = await getActiveProducts(page, ITEMS_PER_PAGE);
          console.log('Products result:', result);
          if (!isCurrentRequest || requestId !== requestRef.current) {
            return;
          }

          if (result) {
            setProducts(prevProducts => {
              if (page === 1) {
                return result.items;
              }

              const existingProductIds = new Set(prevProducts.map(product => product.id));
              const nextProducts = result.items.filter(
                product => !existingProductIds.has(product.id)
              );

              return [...prevProducts, ...nextProducts];
            });
            setHasMore(Boolean(result.hasMore));
            console.log(
              `Found ${result.items.length} active products out of ${result.total} total`
            );
            console.log('Products detail:', JSON.stringify(result.items, null, 2));
          } else {
            setHasMore(false);
          }
        }
      } catch (err) {
        if (isCurrentRequest && requestId === requestRef.current) {
          setError('Failed to load products. Please try again later.');
          setHasMore(false);
          console.error('Error loading products:', err);
        }
      } finally {
        if (isCurrentRequest && requestId === requestRef.current) {
          setLoading(false);
        }
      }
    };

    fetchProducts();

    return () => {
      isCurrentRequest = false;
    };
  }, [page, searchQuery]);

  useEffect(() => {
    const loadMoreElement = loadMoreRef.current;

    if (!loadMoreElement || loading || !hasMore) {
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) {
          setPage(prevPage => prevPage + 1);
        }
      },
      {
        root: null,
        rootMargin: '500px 0px',
        threshold: 0,
      }
    );

    observer.observe(loadMoreElement);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, loading, products.length]);

  // Handle quantity changes
  const incrementQuantity = (productId: string) => {
    setQuantities(prev => ({
      ...prev,
      [productId]: (prev[productId] || 0) + 1,
    }));
  };

  const decrementQuantity = (productId: string) => {
    if (quantities[productId] > 0) {
      setQuantities(prev => ({
        ...prev,
        [productId]: prev[productId] - 1,
      }));
    }
  };

  // Handle search input
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Clear the existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set a new timeout to perform the search after user stops typing
    const timeout = setTimeout(() => {
      const nextSearchQuery = value.trim();

      if (nextSearchQuery === searchQuery && page === 1) {
        return;
      }

      requestRef.current += 1;
      setProducts([]);
      setError(null);
      setHasMore(true);
      setPage(1);
      setSearchQuery(nextSearchQuery);
    }, 500);

    searchTimeoutRef.current = timeout;
  };

  // Handle modal open/close
  const handleOpenModal = (product: Product) => {
    setSelectedProduct(convertToProductWithImages(product));
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Handle adding to cart from the modal
  const handleAddToCartFromModal = (productWithImages: ProductWithImages, quantity: number) => {
    // Find the original product in our products array
    const originalProduct = products.find(p => p.id === productWithImages.id);
    if (originalProduct) {
      addToCart(originalProduct, quantity);
      setQuantities(prev => ({
        ...prev,
        [originalProduct.id]: quantity,
      }));
      setShowAddedAlert(true);
    }
    setIsModalOpen(false);
  };

  // Handle adding directly to cart
  const handleAddToCart = (product: Product, quantity = 1) => {
    addToCart(product, quantity);
    setQuantities(prev => ({
      ...prev,
      [product.id]: (prev[product.id] || 0) + quantity,
    }));
    setShowAddedAlert(true);
  };

  // Go to quote creation page
  const handleGoToQuote = () => {
    navigate('/quotes/new');
  };

  // Handle alert close
  const handleAlertClose = () => {
    setShowAddedAlert(false);
  };

  return (
    <Container maxWidth="lg">
      {/* Main Content */}
      <Box component="main" className={styles.container}>
        {/* Search Bar and Go to Quote Button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <div className={styles.searchContainer}>
            <input
              type="text"
              placeholder="Busque produtos por nome, SKU ou categoria..."
              className={styles.searchInput}
              onChange={handleSearchChange}
            />
            <SearchIcon className={styles.searchIcon} />
          </div>
        </Box>

        {/* Products Section */}
        <Box sx={{ mt: 4, mb: 4 }}>
          <h2 className={styles.sectionHeading}>
            <LocalFireDepartmentIcon className={styles.topProductsIcon} />
            {searchQuery ? 'Resultados da busca' : 'Produtos disponíveis'}
          </h2>

          {/* Loading Indicator or Error Message */}
          {loading && products.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <Typography color="error">{error}</Typography>
            </Box>
          ) : products.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <Typography>
                {searchQuery
                  ? 'Nenhum produto encontrado para esta busca.'
                  : 'Nenhum produto disponível no momento.'}
              </Typography>
            </Box>
          ) : (
            <>
              {/* Product Grid */}
              <div className={styles.productsGrid}>
                {products.map(product => {
                  // No need to calculate price anymore - using the API's pre-calculated price
                  const finalPrice = product.price;

                  return (
                    <div key={product.id} className={styles.productCard}>
                      <div className={styles.cardHeader}>Preço para cliente final | UF SP</div>
                      <div className={styles.cardBody}>
                        <div className={styles.cardTopRow}>
                          <div className={styles.profitBadge}>
                            <LocalFireDepartmentIcon sx={{ fontSize: 16, marginRight: '4px' }} />
                            Comissão Inclusa{' '}
                            {(product.reseller_commission_rate !== undefined
                              ? product.reseller_commission_rate * 100
                              : 8
                            ).toFixed(0)}
                            %
                          </div>
                        </div>
                        <div className={styles.productRow}>
                          <div className={styles.productImageContainer}>
                            <img
                              src={
                                product.images && product.images.length > 0
                                  ? product.images.find(img => img.is_primary)?.image_url ||
                                    product.images[0].image_url
                                  : '/images/placeholder.png'
                              }
                              alt={product.name}
                              width={100}
                              height={100}
                              style={{ objectFit: 'contain' }}
                            />
                          </div>
                          <div className={styles.productInfo}>
                            <div className={styles.productSku}>SKU: {product.sku}</div>
                            <h3 className={styles.productTitle}>{product.name}</h3>
                            <div
                              className={styles.detailsLink}
                              onClick={() => handleOpenModal(product)}
                              style={{ cursor: 'pointer' }}
                            >
                              Ver detalhes do produto
                            </div>
                          </div>
                        </div>
                        <div className={styles.priceContainer}>
                          <div className={styles.price}>{formatCurrency(finalPrice)}</div>
                          <div className={styles.taxInfo}>Impostos + Frete Inclusos</div>
                        </div>
                        <div className={styles.actionsRow}>
                          <button className={styles.quoteButton} onClick={handleGoToQuote}>
                            <ReceiptIcon fontSize="small" className={styles.quoteIcon} />
                            Ir para cotação
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {error && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Typography color="error">{error}</Typography>
                </Box>
              )}

              {loading && (
                <Box className={styles.loadMoreStatus}>
                  <CircularProgress size={28} />
                </Box>
              )}

              <div ref={loadMoreRef} className={styles.loadMoreTrigger} aria-hidden="true" />
            </>
          )}
        </Box>

        {/* Product Details Modal */}
        {selectedProduct && (
          <ProductDetailsModal
            open={isModalOpen}
            onClose={handleCloseModal}
            product={selectedProduct}
            onAddToCart={handleAddToCartFromModal}
          />
        )}

        {/* Added to cart alert */}
        <Snackbar
          open={showAddedAlert}
          autoHideDuration={3000}
          onClose={handleAlertClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleAlertClose}
            severity="success"
            sx={{ width: '100%' }}
            action={
              <Button color="inherit" size="small" onClick={handleGoToQuote}>
                IR PARA COTAÇÃO
              </Button>
            }
          >
            Produto adicionado ao carrinho!
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default ResellerStorePage;
